import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Shield, Wrench, FileText, Building2, Car, Coffee, ShoppingBag, Factory, ArrowUpRight } from 'lucide-react';
export function CaseStudies() {
  const [activeFilter, setActiveFilter] = useState('All');
  const filters = ['All', 'Installation', 'Maintenance', 'TM44/Compliance', 'Hospitality', 'Retail', 'Offices', 'Industrial'];
  const allProjects = [{
    sector: 'Offices',
    title: 'Corporate HQ climate overhaul',
    result: '30% reduction in energy costs and improved staff comfort.',
    tags: ['Installation', 'Energy Efficiency'],
    link: '/contact'
  }, {
    sector: 'Retail',
    title: 'High street store cooling',
    result: 'Zero downtime during peak summer trading.',
    tags: ['Maintenance', 'Retail'],
    link: '/contact'
  }, {
    sector: 'Industrial',
    title: 'Warehouse temperature control',
    result: 'Stabilized storage environment for sensitive goods.',
    tags: ['Installation', 'Industrial'],
    link: '/contact'
  }, {
    sector: 'Hospitality',
    title: 'Boutique hotel AC upgrade',
    result: 'Whisper-quiet operation enhancing guest experience.',
    tags: ['Installation', 'Hospitality'],
    link: '/contact'
  }, {
    sector: 'Offices',
    title: 'Multi-site TM44 assessments',
    result: 'Full compliance achieved across 15 locations.',
    tags: ['TM44/Compliance', 'Offices'],
    link: '/contact'
  }, {
    sector: 'Retail',
    title: 'Supermarket refrigeration & AC',
    result: 'Integrated maintenance improving overall reliability.',
    tags: ['Maintenance', 'Retail'],
    link: '/contact'
  }, {
    sector: 'Industrial',
    title: 'Manufacturing plant ventilation',
    result: 'Improved air quality meeting strict health & safety standards.',
    tags: ['Installation', 'Industrial'],
    link: '/contact'
  }, {
    sector: 'Hospitality',
    title: 'Restaurant chain PPM',
    result: 'Preventative maintenance reducing emergency call-outs by 40%.',
    tags: ['Maintenance', 'Hospitality'],
    link: '/contact'
  }];
  const filteredProjects = activeFilter === 'All' ? allProjects : allProjects.filter(p => p.sector === activeFilter || p.tags.includes(activeFilter));
  return <div className="pt-24 bg-white" data-id="element-813">
      {/* Section 1: Hero */}
      <section className="py-16 md:py-24 bg-primary-light overflow-hidden" data-id="element-814">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-815">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-816">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.5
          }} data-id="element-817">
              <span className="text-accent font-semibold tracking-wider uppercase text-sm mb-4 block" data-id="element-818">
                Case Studies
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-dark mb-6 leading-tight" data-id="element-819">
                Commercial HVAC projects delivered with quality, care and
                long-term value
              </h1>
              <p className="text-xl text-gray-700 mb-8" data-id="element-820">
                Explore a selection of our recent projects, demonstrating our
                commitment to bespoke design, reliable installation, and
                proactive maintenance across various commercial sectors.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-12" data-id="element-821">
                <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-full text-white bg-accent hover:bg-accent/90 transition-colors" data-id="element-822">
                  Discuss your project
                  <ArrowRight className="ml-2 w-5 h-5" data-id="element-823" />
                </Link>
                <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-full text-primary-dark border-2 border-primary-dark hover:bg-primary-dark hover:text-white transition-colors" data-id="element-824">
                  Book a site survey
                </Link>
              </div>

              <div className="flex flex-wrap gap-4" data-id="element-825">
                {['Nationwide commercial projects', 'Design-led solutions', 'Trusted by brands and FM teams'].map((pill, idx) => <div key={idx} className="flex items-center bg-white px-4 py-2 rounded-full shadow-sm" data-id="element-826">
                    <CheckCircle2 className="w-4 h-4 text-accent mr-2" data-id="element-827" />
                    <span className="text-sm font-medium text-primary-dark" data-id="element-828">
                      {pill}
                    </span>
                  </div>)}
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="relative" data-id="element-829">
              <div className="aspect-square md:aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl" data-id="element-830">
                <img src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80" alt="Modern commercial office space" className="w-full h-full object-cover" data-id="element-831" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section 2: Value Strip */}
      <section className="py-12 border-b border-gray-200" data-id="element-832">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-833">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-834">
            {[{
            icon: Wrench,
            title: 'Bespoke commercial design',
            desc: 'Tailored solutions for unique environments.'
          }, {
            icon: Shield,
            title: 'Installations built to last',
            desc: 'High-quality equipment and rigorous standards.'
          }, {
            icon: FileText,
            title: 'Detailed reporting & compliance',
            desc: 'Comprehensive documentation for peace of mind.'
          }, {
            icon: Building2,
            title: 'Trusted by recognised brands',
            desc: 'Partnering with leading names across sectors.'
          }].map((value, idx) => <div key={idx} className="flex items-start" data-id="element-835">
                <div className="w-10 h-10 bg-primary-light rounded-lg flex items-center justify-center flex-shrink-0 mr-4" data-id="element-836">
                  <value.icon className="w-5 h-5 text-accent" data-id="element-837" />
                </div>
                <div data-id="element-838">
                  <h3 className="font-bold text-primary-dark mb-1" data-id="element-839">
                    {value.title}
                  </h3>
                  <p className="text-sm text-gray-600" data-id="element-840">{value.desc}</p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Section 3: Featured Case Studies (Bento) */}
      <section className="py-20 bg-surface" data-id="element-841">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-842">
          <div className="text-center mb-12" data-id="element-843">
            <h2 className="text-3xl md:text-4xl font-bold text-primary-dark mb-4" data-id="element-844">
              Featured projects
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-845">
              Deep dives into some of our most impactful commercial HVAC
              installations.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-846">
            {/* Large Card */}
            <Link to="/case-studies/formula-one" className="lg:col-span-2 group block" data-id="element-847">
              <div className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 h-full flex flex-col" data-id="element-848">
                <div className="relative h-64 sm:h-80 overflow-hidden" data-id="element-849">
                  <img src="https://cdn.magicpatterns.com/uploads/25TP9PqASwBfhD3FYFT9Py/pasted-image.jpg" alt="Automotive engineering" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-850" />
                  <div className="absolute top-4 left-4 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-sm font-semibold text-primary-dark" data-id="element-851">
                    Engineering / Performance Environment
                  </div>
                </div>
                <div className="p-8 flex-grow flex flex-col" data-id="element-852">
                  <h3 className="text-2xl font-bold text-primary-dark mb-3 group-hover:text-accent transition-colors" data-id="element-853">
                    Precision temperature control for a high-performance working
                    environment
                  </h3>
                  <p className="text-gray-600 mb-6 flex-grow" data-id="element-854">
                    Designed to support comfort, air quality and tightly
                    controlled workspace conditions in a demanding engineering
                    setting.
                  </p>
                  <div className="flex flex-wrap gap-2 mb-6" data-id="element-855">
                    {['Bespoke design', 'Air quality focus', 'Performance workspace'].map((tag, i) => <span key={i} className="bg-surface px-3 py-1 rounded-full text-xs font-medium text-gray-600" data-id="element-856">
                        {tag}
                      </span>)}
                  </div>
                  <div className="flex items-center text-accent font-semibold" data-id="element-857">
                    View case study <ArrowRight className="ml-2 w-4 h-4" data-id="element-858" />
                  </div>
                </div>
              </div>
            </Link>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-6" data-id="element-859">
              {/* Medium Card 1 */}
              <Link to="/case-studies/subway" className="group block" data-id="element-860">
                <div className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 h-full flex flex-col" data-id="element-861">
                  <div className="relative h-48 overflow-hidden" data-id="element-862">
                    <img src="https://cdn.magicpatterns.com/uploads/6woGoZZ58PZurSZ7Y9FmxV/subway-welcome.jpg" alt="Subway" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-863" />
                    <div className="absolute top-4 left-4 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-xs font-semibold text-primary-dark" data-id="element-864">
                      Hospitality / Quick Service
                    </div>
                  </div>
                  <div className="p-6 flex-grow flex flex-col" data-id="element-865">
                    <h3 className="text-lg font-bold text-primary-dark mb-2 group-hover:text-accent transition-colors" data-id="element-866">
                      Reliable cooling for a busy customer-facing food service
                      environment
                    </h3>
                    <p className="text-sm text-gray-600 mb-4 flex-grow" data-id="element-867">
                      Commercial climate solution designed to support customer
                      comfort, staff working conditions and day-to-day
                      reliability.
                    </p>
                    <div className="flex flex-wrap gap-2 mb-4" data-id="element-868">
                      {['Customer-facing', 'Reliable cooling', 'Commercial install'].map((tag, i) => <span key={i} className="bg-surface px-2 py-1 rounded-full text-[10px] font-medium text-gray-600" data-id="element-869">
                          {tag}
                        </span>)}
                    </div>
                    <div className="flex items-center text-accent font-semibold text-sm mt-auto" data-id="element-870">
                      View case study <ArrowRight className="ml-1 w-4 h-4" data-id="element-871" />
                    </div>
                  </div>
                </div>
              </Link>

              {/* Medium Card 2 */}
              <Link to="/case-studies/process-cooling" className="group block" data-id="element-872">
                <div className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 h-full flex flex-col" data-id="element-873">
                  <div className="relative h-48 overflow-hidden" data-id="element-874">
                    <img src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80" alt="Process Cooling" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-875" />
                    <div className="absolute top-4 left-4 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-xs font-semibold text-primary-dark" data-id="element-876">
                      Industrial / Process Environment
                    </div>
                  </div>
                  <div className="p-6 flex-grow flex flex-col" data-id="element-877">
                    <h3 className="text-lg font-bold text-primary-dark mb-2 group-hover:text-accent transition-colors" data-id="element-878">
                      A cooling solution that supported operations and paid back
                      fast
                    </h3>
                    <p className="text-sm text-gray-600 mb-4 flex-grow" data-id="element-879">
                      Practical application-focused cooling that helped improve
                      process control and reduce waste in production.
                    </p>
                    <div className="flex flex-wrap gap-2 mb-4" data-id="element-880">
                      {['Operational ROI', 'Process cooling', 'Commercial application'].map((tag, i) => <span key={i} className="bg-surface px-2 py-1 rounded-full text-[10px] font-medium text-gray-600" data-id="element-881">
                          {tag}
                        </span>)}
                    </div>
                    <div className="flex items-center text-accent font-semibold text-sm mt-auto" data-id="element-882">
                      View case study <ArrowRight className="ml-1 w-4 h-4" data-id="element-883" />
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Brands by Sector */}
      <section className="py-20" data-id="element-884">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-885">
          <div className="text-center mb-12" data-id="element-886">
            <h2 className="text-3xl md:text-4xl font-bold text-primary-dark mb-4" data-id="element-887">
              Brands and businesses we've supported
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-888">
              Delivering tailored climate solutions across diverse commercial
              sectors.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-889">
            <div className="bg-surface p-8 rounded-3xl" data-id="element-890">
              <div className="flex items-center mb-4" data-id="element-891">
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center mr-4" data-id="element-892">
                  <Car className="w-5 h-5 text-accent" data-id="element-893" />
                </div>
                <h3 className="text-xl font-bold text-primary-dark" data-id="element-894">
                  Automotive & engineering
                </h3>
              </div>
              <p className="text-gray-600 mb-6" data-id="element-895">
                Experience in operational, engineering and performance-critical
                environments.
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-896">
                {['JLR', 'Peugeot', 'Nissan', 'Performance Engineering'].map((brand, i) => <span key={i} className="bg-white px-3 py-1 rounded-full text-sm font-medium text-primary-dark border border-gray-200" data-id="element-897">
                      {brand}
                    </span>)}
              </div>
            </div>

            <div className="bg-surface p-8 rounded-3xl" data-id="element-898">
              <div className="flex items-center mb-4" data-id="element-899">
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center mr-4" data-id="element-900">
                  <Building2 className="w-5 h-5 text-accent" data-id="element-901" />
                </div>
                <h3 className="text-xl font-bold text-primary-dark" data-id="element-902">
                  Hospitality & hotels
                </h3>
              </div>
              <p className="text-gray-600 mb-6" data-id="element-903">
                Guest-facing hospitality settings where comfort and uptime are
                essential.
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-904">
                {['Ramada', 'Days Inn'].map((brand, i) => <span key={i} className="bg-white px-3 py-1 rounded-full text-sm font-medium text-primary-dark border border-gray-200" data-id="element-905">
                    {brand}
                  </span>)}
              </div>
            </div>

            <div className="bg-surface p-8 rounded-3xl" data-id="element-906">
              <div className="flex items-center mb-4" data-id="element-907">
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center mr-4" data-id="element-908">
                  <ShoppingBag className="w-5 h-5 text-accent" data-id="element-909" />
                </div>
                <h3 className="text-xl font-bold text-primary-dark" data-id="element-910">
                  Retail & public-facing
                </h3>
              </div>
              <p className="text-gray-600 mb-6" data-id="element-911">
                Commercial environments where customer experience and dependable
                climate control matter.
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-912">
                {['Post Office', 'Specsavers', 'Cotswold Company', 'Toni & Guy'].map((brand, i) => <span key={i} className="bg-white px-3 py-1 rounded-full text-sm font-medium text-primary-dark border border-gray-200" data-id="element-913">
                    {brand}
                  </span>)}
              </div>
            </div>

            <div className="bg-surface p-8 rounded-3xl" data-id="element-914">
              <div className="flex items-center mb-4" data-id="element-915">
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center mr-4" data-id="element-916">
                  <Coffee className="w-5 h-5 text-accent" data-id="element-917" />
                </div>
                <h3 className="text-xl font-bold text-primary-dark" data-id="element-918">
                  Food, beverage & quick service
                </h3>
              </div>
              <p className="text-gray-600 mb-6" data-id="element-919">
                Fast-paced customer-facing spaces where comfort and reliable
                performance matter.
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-920">
                {['Subway', 'Wingers', 'Starbucks', 'Black Sheep Coffee', 'Esquires', 'Pepes Piri Piri', "Papa John's"].map((brand, i) => <span key={i} className="bg-white px-3 py-1 rounded-full text-sm font-medium text-primary-dark border border-gray-200" data-id="element-921">
                    {brand}
                  </span>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: All Case Studies Grid */}
      <section className="py-20 bg-primary-light/30" data-id="element-922">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-923">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-10 gap-6" data-id="element-924">
            <h2 className="text-3xl font-bold text-primary-dark" data-id="element-925">
              Explore more projects
            </h2>

            <div className="flex flex-wrap gap-2" data-id="element-926">
              {filters.map(filter => <button key={filter} onClick={() => setActiveFilter(filter)} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeFilter === filter ? 'bg-primary-dark text-white' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'}`} data-id="element-927">
                  {filter}
                </button>)}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-928">
            {filteredProjects.map((project, idx) => <motion.div key={idx} layout initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.9
          }} transition={{
            duration: 0.3
          }} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col h-full group hover:shadow-md transition-shadow" data-id="element-929">
                <div className="text-xs font-semibold text-accent mb-3 uppercase tracking-wider" data-id="element-930">
                  {project.sector}
                </div>
                <h3 className="text-lg font-bold text-primary-dark mb-3 group-hover:text-accent transition-colors" data-id="element-931">
                  {project.title}
                </h3>
                <p className="text-gray-600 text-sm mb-6 flex-grow" data-id="element-932">
                  {project.result}
                </p>
                <div className="flex flex-wrap gap-2 mb-6" data-id="element-933">
                  {project.tags.map((tag, i) => <span key={i} className="bg-surface px-2 py-1 rounded-md text-[10px] font-medium text-gray-500" data-id="element-934">
                      {tag}
                    </span>)}
                </div>
                <Link to={project.link} className="inline-flex items-center text-sm font-semibold text-primary-dark group-hover:text-accent transition-colors mt-auto" data-id="element-935">
                  View case study <ArrowUpRight className="ml-1 w-4 h-4" data-id="element-936" />
                </Link>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Section 6: Final CTA Band */}
      <section className="py-20 bg-primary-dark text-white text-center" data-id="element-937">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-938">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-939">
            Planning a similar project?
          </h2>
          <p className="text-xl text-gray-300 mb-10" data-id="element-940">
            Whether you need a bespoke installation, reliable maintenance, or
            TM44 compliance support, our commercial team is ready to help.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-941">
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-full text-white bg-accent hover:bg-accent/90 transition-colors" data-id="element-942">
              Book a site survey
              <ArrowRight className="ml-2 w-5 h-5" data-id="element-943" />
            </Link>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-full text-white border-2 border-white/20 hover:bg-white/10 transition-colors" data-id="element-944">
              Talk to our commercial team
            </Link>
          </div>
        </div>
      </section>
    </div>;
}